import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import Icons from "@/assets";
import { stringToColorSet } from "@/utils/helper";
import { User } from "@/types/common";
import { Close, InfoOutlined, Logout, MoreVert } from "@mui/icons-material";
import LogoutDlg from "./LogoutDlg";

interface UserInfoCardProps {
  data: User | null;
  hideActions?: boolean;
}

const styles = {
  label: { color: "#626262", fontSize: { md: "1.39vw" } },
  avatar: {
    width: { xs: 40, md: "4vw" },
    height: { xs: 40, md: "4vw" },
    fontWeight: 700,
    fontSize: { xs: 18, md: "1.5vw" },
  },
  userName: {
    fontSize: { xs: "3.88vw", sm: 16 },
    color: "#FFFFFF",
    fontWeight: 600,
  },
  statValue: {
    fontSize: { xs: "4.06vw", sm: "2.64vw", md: "1.64vw" },
    fontWeight: 700,
  },
  statLabel: {
    color: "#626262",
    fontWeight: 500,
    fontSize: { xs: "3.8vw", sm: "1.68vw", md: "1.2vw" },
  },
  trustScoreLabel: {
    fontSize: { xs: "2.6vw", sm: "1.2vw", md: "0.86vw" },
    color: "#626262",
    fontWeight: 400,
  },
  trustScoreValue: {
    fontSize: { xs: "4.36vw", sm: "2.4vw", md: "1.4vw" },
    fontWeight: 700,
    color: "#74CE6A",
    lineHeight: 1,
  },
};

const StatItem = ({
  value,
  label,
  color,
}: {
  value: number | string;
  label: string;
  color: string;
}) => (
  <Box sx={{ textAlign: "center" }}>
    <Typography sx={{ ...styles.statValue, color }}>{value}</Typography>
    <Typography sx={styles.statLabel}>{label}</Typography>
  </Box>
);

const UserInfoCard: React.FC<UserInfoCardProps> = ({ data, hideActions }) => {
  const {
    username,
    contribution_count = 0,
    reviewed_count = 0,
    pending_count = 0,
    best_contribution_count = 0,
  } = data ?? {};

  const total = Number(contribution_count) || 0;
  const reviewed = Number(reviewed_count) || 0;
  const pending = Number(pending_count) || 0;
  const bestCount = Number(best_contribution_count) || 0;

  // Logic for linear progress bar and milestone display
  const milestoneDenominator =
    total === 0 ? 1 : total < 5 ? 5 : total < 10 ? 10 : total < 15 ? 15 : total;
  const progressPercentage = Math.round((total / milestoneDenominator) * 100);

  const trustScoreCircle =
    total === 0 ? 0 : Math.round((bestCount / total) * 100);

  const colorSet = stringToColorSet(username || "Name");
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [trustOpen, setTrustOpen] = useState(false);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          pt: "8%",
          pb: "4%",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", columnGap: "2vw" }}>
          <Avatar
            sx={{
              ...styles.avatar,
              bgcolor: colorSet.bg,
              border: `1px solid ${colorSet.border}`,
              color: colorSet.text,
            }}
          >
            {username?.[0]?.toUpperCase()}
          </Avatar>
          <Typography sx={styles.userName}>{username}</Typography>
        </Box>
        {!hideActions && (
          <IconButton onClick={handleMenuOpen} aria-label="menu">
            <MoreVert sx={{ color: "#FFFFFF" }} fontSize="medium" />
          </IconButton>
        )}
      </Box>

      <Card
        sx={{
          borderRadius: "8px",
          boxShadow: "0 4px 50px rgba(0, 0, 0, 0.05)",
          overflow: "visible",
        }}
      >
        <CardContent sx={{ p: 2, "&:last-child": { pb: 3 } }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 2 }}>
            <Box
              sx={{
                display: "flex",
                flexFlow: "wrap",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 1,
                flexGrow: 1,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                <Box
                  component="img"
                  src={Icons.ContributionsMade}
                  sx={{
                    height: { xs: 20, md: "1.8vw" },
                    width: { xs: 20, md: "1.8vw" },
                  }}
                />
                <Typography
                  sx={{
                    color: "#9443DF",
                    fontSize: { xs: "3.5vw", sm: "1.68vw" },
                    fontWeight: 800,
                  }}
                >
                  {total === 0
                    ? "0/1"
                    : total < 5
                      ? `${total}/5`
                      : total < 10
                        ? `${total}/10`
                        : total < 15
                          ? `${total}/15`
                          : total}
                </Typography>
                <Typography
                  sx={{
                    color: "#4A4A4A",
                    fontSize: { xs: "3.5vw", sm: "1.68vw" },
                    fontWeight: 500,
                  }}
                >
                  contributions made
                </Typography>
              </Box>

              <Box
                sx={{
                  height: 12,
                  width: "100%",
                  bgcolor: "#F0DEFF",
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    height: "100%",
                    width: `${progressPercentage}%`,
                    background:
                      "linear-gradient(90deg, #9D79F4 0%, #7747FB 100%)",
                    borderRadius: 6,
                  }}
                />
              </Box>
              <Typography
                sx={{
                  fontSize: { xs: "3vw", sm: 13, md: "1vw" },
                  color: "#626262",
                  fontWeight: 700,
                  lineHeight: 1.4,
                }}
              >
                Start contributing, earn points, and level up with new badges.
              </Typography>
            </Box>

            {/* Trust Score Circle */}
            <Box
              sx={{
                width: { xs: 76, md: "6.5vw" },
                height: { xs: 76, md: "6.5vw" },
                borderRadius: "50%",
                background: `conic-gradient(#86efac ${trustScoreCircle}%, #f0fdf4 0)`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                flexShrink: 0,
                mt: -1,
              }}
            >
              <Box
                sx={{
                  position: "absolute",
                  width: "85%",
                  height: "85%",
                  bgcolor: "#E2FFDE",
                  borderRadius: "50%",
                  zIndex: 1,
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 0.25,
                  zIndex: 2,
                  textAlign: "center",
                }}
              >
                <Typography sx={styles.trustScoreLabel}>Trust Score</Typography>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-evenly",
                    alignItems: "center",
                  }}
                >
                  <Typography sx={styles.trustScoreValue}>
                    {trustScoreCircle}
                  </Typography>
                  <InfoOutlined
                    onClick={() => setTrustOpen(true)}
                    sx={{ width: 16, height: 16, cursor: "pointer" }}
                  />
                </Box>
              </Box>
            </Box>
          </Box>

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              width: "100%",
            }}
          >
            <StatItem value={reviewed} label="Reviewed" color="#74CE6A" />
            <StatItem value={bestCount} label="Best Contribution" color="#7747FB" />
            <StatItem value={pending} label="Under Review" color="#F2B12F" />
          </Box>
        </CardContent>
      </Card>

      <Dialog
        open={trustOpen}
        onClose={() => setTrustOpen(false)}
        aria-labelledby="trust-score-dialog-title"
        maxWidth="xs"
        fullWidth
      >
        <IconButton
          aria-label="close"
          onClick={() => setTrustOpen(false)}
          onTouchEnd={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setTrustOpen(false);
          }}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            zIndex: 1000,
            cursor: "pointer",
            touchAction: "manipulation",
            WebkitTouchCallout: "none",
            WebkitUserSelect: "none",
          }}
          size="small"
        >
          <Close />
        </IconButton>
        <DialogContent dividers sx={{ textAlign: "center", p: 2 }}>
          <Box
            component="img"
            src={Icons.TrustScore}
            sx={{
              height: { xs: 112, md: "1.8vw" },
              width: { xs: 112, md: "1.8vw" },
              mb: 1,
            }}
          />
          <Typography
            sx={{ fontSize: { xs: "4.5vw", sm: 16 }, fontWeight: 700, mb: 0.7 }}
          >
            Trust Score
          </Typography>
          <Typography
            sx={{ fontSize: { xs: "3.5vw", sm: 16 }, color: "#4A4A4A" }}
          >
            Your Trust Score increases when your contribution is selected as a{" "}
            <Box component="strong" sx={{ color: "#000", fontWeight: 700 }}>
              Best Contribution
            </Box>{" "}
            for sharing accurate details such as photos, videos, and notes. Keep
            contributing quality information to grow your score.
          </Typography>
        </DialogContent>
      </Dialog>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
        PaperProps={{
          elevation: 3,
          sx: { borderRadius: 2, minWidth: 150 },
        }}
      >
        <MenuItem onClick={() => setConfirmOpen(true)}>
          <ListItemIcon>
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText>Logout</ListItemText>
        </MenuItem>
      </Menu>

      <LogoutDlg open={confirmOpen} onClose={() => setConfirmOpen(false)} />
    </>
  );
};

export default UserInfoCard;
